import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';

import type { RealtimeConnectionState } from '../../core/realtime/realtime-state.model';
import { FreshnessService } from '../../core/time/freshness.service';
import { FaIconComponent, faClock, faTriangleExclamation } from '../icons/app-icons.registry';

type ConnectionState = RealtimeConnectionState | 'disconnected';

/**
 * Non-blocking freshness indication (FR-017..FR-021): inline, never modal,
 * politely announced when its tier changes. Renders nothing when
 * no page has published a data instant. An optional connectionState input
 * forces the degraded tier (AITH-31 supplies it later); with no supplier the
 * reading is purely time-derived.
 */
@Component({
  selector: 'app-freshness-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent],
  template: `
    @if (reading(); as reading) {
      <span class="freshness freshness-{{ reading.tier }}">
        <fa-icon [icon]="reading.tier === 'normal' ? clockIcon : alertIcon" aria-hidden="true" />
        <span class="freshness-tier">{{ tierText(reading.tier, reading.displayText) }}</span>
        <span class="visually-hidden" aria-live="polite">{{ liveStatus() }}</span>
      </span>
    }
  `,
  styles: `
    .freshness {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2xs);
      font-size: var(--font-size-sm);
      color: var(--color-text-muted);
    }
    // Ruling 3: the warning tier uses the text-safe darkened amber.
    .freshness-warning {
      color: var(--color-accent-text);
    }
    // Ruling 2's derived token: degraded uses the text-safe error hue.
    .freshness-degraded {
      color: var(--color-error-text);
    }
    @media (max-width: 767px) {
      .freshness {
        font-size: var(--font-size-xs);
        white-space: nowrap;
      }
    }
  `,
})
export class FreshnessIndicatorComponent {
  /** Epoch ms the page's data was produced; null → render nothing. */
  readonly producedAt = input.required<number | null>();
  /** Reconnecting and fallback live-update states force the degraded tier. */
  readonly connectionState = input<ConnectionState | null>(null);

  private readonly freshness = inject(FreshnessService);

  protected readonly clockIcon = faClock;
  protected readonly alertIcon = faTriangleExclamation;

  protected readonly reading = computed(() => {
    const producedAt = this.producedAt();
    if (producedAt === null) return null;
    const reading = this.freshness.read(producedAt);
    if (['disconnected', 'reconnecting', 'fallback'].includes(this.connectionState() ?? '')) {
      return { ...reading, tier: 'degraded' as const };
    }
    return reading;
  });

  protected readonly liveStatus = signal('');
  private lastAnnouncementKey: string | null = null;

  constructor() {
    effect(() => {
      const reading = this.reading();
      if (reading === null) {
        this.lastAnnouncementKey = null;
        this.liveStatus.set('');
      } else {
        const state = this.connectionState();
        const announcementKey = `${state ?? 'freshness'}:${reading.tier}`;
        if (announcementKey === this.lastAnnouncementKey) return;
        this.lastAnnouncementKey = announcementKey;
        this.liveStatus.set(this.statusText(reading.tier, state));
      }
    });
  }

  protected tierText(tier: 'normal' | 'warning' | 'degraded', displayText: string): string {
    switch (this.connectionState()) {
      case 'disabled':
        return `Live updates off - Updated ${displayText}`;
      case 'connecting':
        return `Live updates connecting - Updated ${displayText}`;
      case 'connected':
        return `Live updates connected - Updated ${displayText}`;
      case 'reconnecting':
        return `Live updates reconnecting - Last update ${displayText}`;
      case 'fallback':
        return `Query fallback - Last update ${displayText}`;
    }
    if (tier === 'degraded') {
      return `Live updates interrupted — reconnecting (last update ${displayText})`;
    }
    return `Updated ${displayText}`;
  }

  private statusText(
    tier: 'normal' | 'warning' | 'degraded',
    state: ConnectionState | null,
  ): string {
    // Announce BOTH dimensions (a11y): a connected-but-stale reading must
    // remain audible — the tier text changes the live-region value where the
    // state-only text would not (Suppressed review comment, FR-019).
    const tierFragment =
      tier === 'degraded'
        ? 'Data freshness degraded.'
        : tier === 'warning'
          ? 'Data freshness warning.'
          : null;
    switch (state) {
      case 'disabled':
        return tierFragment === null ? 'Live updates disabled.' : `Live updates disabled. ${tierFragment}`;
      case 'connecting':
        return tierFragment === null ? 'Live updates connecting.' : `Live updates connecting. ${tierFragment}`;
      case 'connected':
        return tierFragment === null ? 'Live updates connected.' : `Live updates connected. ${tierFragment}`;
      case 'reconnecting':
      case 'disconnected':
        return tierFragment === null ? 'Live updates reconnecting.' : `Live updates reconnecting. ${tierFragment}`;
      case 'fallback':
        return tierFragment === null ? 'Using query fallback.' : `Using query fallback. ${tierFragment}`;
    }
    if (tier === 'degraded') return 'Live updates interrupted; reconnecting.';
    if (tier === 'warning') return 'Data freshness warning.';
    return 'Data is current.';
  }
}
